/**
 * ToolsPage Component
 * Main page for managing MCP Actions (Tools)
 * UI aligned with AgentsPage card-based design
 */

import * as React from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { ToolForm } from '@/components/tools/ToolForm'
import { ToolsExplorerContentManager } from '@/components/tools/ToolsExplorerContentManager'
import { toolsService } from '@/services/toolsService'
import { useToolFolders } from '@/hooks/use-tool-folders'
import { useToolPlacements } from '@/hooks/use-tool-placements'
import { loadToolIconsMap } from '@/lib/tool-icons'
import { useIsMobile } from '@/hooks/use-mobile'
import { cn } from '@/lib/utils'
import type {
  MCPAction,
} from '@/types/tool'
import {
  ArrowLeft,
  FolderPlus,
  LayoutGrid,
  List,
  Plus,
  Search,
} from 'lucide-react'

/* ------------------------------------------------------------------ */
/*  Emoji persistence (localStorage, no backend changes)               */
/* ------------------------------------------------------------------ */

function toErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message) return error.message
  return 'Ha ocurrido un error inesperado.'
}

export function ToolsPage() {
  const navigate = useNavigate()
  const { lang } = useParams<{ lang: string }>()
  const isMobile = useIsMobile()
  const langPrefix = `/${lang || 'en'}`

  const [tools, setTools] = React.useState<MCPAction[]>([])
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)
  const [formOpen, setFormOpen] = React.useState(false)
  const [selectedTool, setSelectedTool] = React.useState<MCPAction | null>(null)
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false)
  const [toolToDelete, setToolToDelete] = React.useState<MCPAction | null>(null)
  const { folders, loading: foldersLoading, createFolder, updateFolder, deleteFolder } = useToolFolders()
  const { placements, loading: placementsLoading, moveTool, bulkMoveTools, prunePlacementsForTools } = useToolPlacements()
  const [toolIcons, setToolIcons] = React.useState(loadToolIconsMap)

  // Filters
  const [searchQuery, setSearchQuery] = React.useState('')
  const [typeFilter, setTypeFilter] = React.useState<string>('all')
  const [enabledFilter, setEnabledFilter] = React.useState<string>('all')
  // Las internas son las que operan la plataforma -las que genera el contrato
  // para el Brain-. Mezcladas con las del negocio estaban ahi y no se veian.
  const [ambitoFilter, setAmbitoFilter] = React.useState<'all' | 'business' | 'internal'>('all')
  const [mobileViewMode, setMobileViewMode] = React.useState<'grid' | 'list'>('grid')
  const [mobileCreateFolderOpen, setMobileCreateFolderOpen] = React.useState(false)
  const [mobileCurrentFolderId, setMobileCurrentFolderId] = React.useState<string | null>(null)
  const mobileCurrentFolderName = React.useMemo(
    () => folders.find((folder) => folder.id === mobileCurrentFolderId)?.name ?? null,
    [folders, mobileCurrentFolderId]
  )

  // Load data
  const loadData = React.useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      // Sin las generadas a proposito: esta pagina las edita y las borra, y la API
      // las rechaza con 403 porque salen del contrato. Se asignan desde la ficha
      // del agente, que es donde solo se marcan.
      const toolsData = await toolsService.listActions()
      setTools(toolsData)
    } catch (loadError) {
      console.error('Error loading tools:', loadError)
      setError(toErrorMessage(loadError))
    } finally {
      setLoading(false)
    }
  }, [])

  React.useEffect(() => {
    loadData()
  }, [loadData])

  React.useEffect(() => {
    if (!tools.length) return
    prunePlacementsForTools(tools.map((tool) => tool.id))
  }, [tools, prunePlacementsForTools])

  const handleCreate = () => {
    navigate(`${langPrefix}/tool-studio`)
  }

  const handleEdit = async (tool: MCPAction) => {
    try {
      setError(null)
      const detail = await toolsService.getAction(tool.id)
      setSelectedTool(detail)
      setFormOpen(true)
    } catch (loadError) {
      console.error('Error loading tool detail:', loadError)
      setError(toErrorMessage(loadError))
    }
  }

  const handleDelete = (tool: MCPAction) => {
    setToolToDelete(tool)
    setDeleteDialogOpen(true)
  }

  const confirmDelete = async () => {
    if (!toolToDelete) return
    try {
      await toolsService.deleteAction(toolToDelete.id)
      await loadData()
      setDeleteDialogOpen(false)
      setToolToDelete(null)
    } catch (deleteError) {
      console.error('Error deleting tool:', deleteError)
      setError(toErrorMessage(deleteError))
    }
  }

  const handleToggleEnabled = async (tool: MCPAction) => {
    try {
      if (tool.is_enabled) {
        await toolsService.disableAction(tool.id)
      } else {
        await toolsService.enableAction(tool.id)
      }
      await loadData()
    } catch (toggleError) {
      console.error('Error toggling tool:', toggleError)
      setError(toErrorMessage(toggleError))
    }
  }

  const handleFormSuccess = () => {
    setToolIcons(loadToolIconsMap())
    loadData()
  }

  // Filter tools
  const filteredTools = React.useMemo(() => {
    return tools.filter((tool) => {
      if (
        searchQuery &&
        !tool.name.toLowerCase().includes(searchQuery.toLowerCase()) &&
        !tool.display_name.toLowerCase().includes(searchQuery.toLowerCase()) &&
        !tool.description?.toLowerCase().includes(searchQuery.toLowerCase())
      ) {
        return false
      }
      if (typeFilter !== 'all') {
        if (typeFilter === 'webhook') {
          if (tool.execution_type !== 'webhook' && tool.execution_type !== 'python') return false
        } else if (tool.execution_type !== typeFilter) {
          return false
        }
      }
      if (enabledFilter === 'enabled' && !tool.is_enabled) return false
      if (enabledFilter === 'disabled' && tool.is_enabled) return false
      if (ambitoFilter === 'internal' && tool.classification !== 'internal') return false
      if (ambitoFilter === 'business' && tool.classification === 'internal') return false
      return true
    })
  }, [tools, searchQuery, typeFilter, enabledFilter, ambitoFilter])

  const internasCount = React.useMemo(
    () => tools.filter((tool) => tool.classification === 'internal').length,
    [tools]
  )

  return (
    <div className="container mx-auto space-y-8 p-3 md:p-6">
      {/* Header */}
      <section>
        {isMobile ? (
          <div className="space-y-2">
            <h1 className="text-2xl font-semibold tracking-tight">Herramientas</h1>
            <div className="flex items-center gap-2">
              {mobileCurrentFolderId && (
                <Button
                  type="button"
                  size="icon-sm"
                  variant="ghost"
                  className="shrink-0"
                  onClick={() => {
                    const parentId = folders.find((folder) => folder.id === mobileCurrentFolderId)?.parent_id ?? null
                    setMobileCurrentFolderId(parentId)
                  }}
                  aria-label="Volver"
                >
                  <ArrowLeft className="h-4 w-4" />
                </Button>
              )}
              <div className="relative min-w-0 flex-1">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  placeholder={mobileCurrentFolderName ? `En ${mobileCurrentFolderName}` : "Nombre visible, interno o descripción"}
                  className="h-9 pl-9"
                />
              </div>
              <Button
                type="button"
                size="icon-sm"
                className="shrink-0"
                onClick={handleCreate}
                aria-label="Crear Herramienta"
              >
                <Plus className="h-4 w-4" />
              </Button>
              <Button
                type="button"
                size="icon-sm"
                variant="outline"
                className="shrink-0"
                onClick={() => setMobileCreateFolderOpen((prev) => !prev)}
                aria-label="Nueva carpeta"
              >
                <FolderPlus className="h-4 w-4" />
              </Button>
              <div className="inline-flex shrink-0 items-center gap-1 rounded-md border bg-card p-1">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-xs"
                  className={cn(mobileViewMode === 'grid' && 'bg-background')}
                  onClick={() => setMobileViewMode('grid')}
                  title="Vista cards"
                >
                  <LayoutGrid className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-xs"
                  className={cn(mobileViewMode === 'list' && 'bg-background')}
                  onClick={() => setMobileViewMode('list')}
                  title="Vista lista"
                >
                  <List className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h1 className="text-2xl font-semibold tracking-tight">Herramientas</h1>
              <p className="mt-1 text-sm text-muted-foreground">
                Gestiona las herramientas (MCP Actions) disponibles para los agentes
              </p>
            </div>
            <Button size="lg" onClick={handleCreate}>
              <Plus className="mr-2 h-4 w-4" />
              Crear Herramienta
            </Button>
          </div>
        )}
      </section>

      {/* Error alert */}
      {error && (
        <Alert variant="destructive">
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Tabs value={ambitoFilter} onValueChange={(value) => setAmbitoFilter(value as typeof ambitoFilter)}>
        <div className="border-b">
          <TabsList className="h-11 rounded-none bg-transparent p-0">
            <TabsTrigger value="all" className="px-4 data-[state=active]:border-b-2 data-[state=active]:border-primary">
              Todas
            </TabsTrigger>
            <TabsTrigger value="business" className="px-4 data-[state=active]:border-b-2 data-[state=active]:border-primary">
              Del negocio
            </TabsTrigger>
            <TabsTrigger value="internal" className="px-4 data-[state=active]:border-b-2 data-[state=active]:border-primary">
              Internas{internasCount > 0 ? ` (${internasCount})` : ''}
            </TabsTrigger>
          </TabsList>
        </div>
      </Tabs>

      {ambitoFilter === 'internal' && (
        <Alert>
          <AlertTitle>Herramientas que operan la plataforma</AlertTitle>
          <AlertDescription>
            Las genera el contrato de la API para el Brain. Se pueden asignar a otros agentes,
            pero solo a los de audiencia interna: un agente publico que atienda a clientes no
            puede llevarlas.
          </AlertDescription>
        </Alert>
      )}

      <ToolsExplorerContentManager
        tools={filteredTools}
        folders={folders}
        placements={placements}
        loading={loading || foldersLoading || placementsLoading}
        searchQuery={searchQuery}
        onSearchQueryChange={setSearchQuery}
        typeFilter={typeFilter}
        onTypeFilterChange={setTypeFilter}
        enabledFilter={enabledFilter}
        onEnabledFilterChange={setEnabledFilter}
        onCreateFolder={async ({ name, parent_id }) => {
          await createFolder({ name, parent_id })
        }}
        onMoveFolder={async (folderId, parentId) => {
          await updateFolder(folderId, { parent_id: parentId })
        }}
        onDeleteFolder={async (folderId) => {
          await deleteFolder(folderId)
        }}
        onMoveTool={async (toolId, folderId) => {
          await moveTool(toolId, folderId)
        }}
        onBulkMoveTools={bulkMoveTools}
        onEditTool={handleEdit}
        onDeleteTool={handleDelete}
        onDeleteManyTools={async (toolIds) => {
          try {
            await Promise.all(toolIds.map((toolId) => toolsService.deleteAction(toolId)))
            await loadData()
          } catch (deleteError) {
            console.error('Error deleting selected tools:', deleteError)
            setError(toErrorMessage(deleteError))
          }
        }}
        onToggleTool={handleToggleEnabled}
        onCreateTool={handleCreate}
        toolIcons={toolIcons}
        viewMode={isMobile ? mobileViewMode : undefined}
        onViewModeChange={isMobile ? setMobileViewMode : undefined}
        showCreateFolder={isMobile ? mobileCreateFolderOpen : undefined}
        onShowCreateFolderChange={isMobile ? setMobileCreateFolderOpen : undefined}
        hideMobileToolbar={isMobile}
        currentFolderId={isMobile ? mobileCurrentFolderId : undefined}
        onCurrentFolderIdChange={isMobile ? setMobileCurrentFolderId : undefined}
      />

      {/* Tool Form Dialog */}
      <ToolForm
        open={formOpen}
        onOpenChange={setFormOpen}
        tool={selectedTool}
        onSuccess={handleFormSuccess}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar herramienta</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción eliminará la herramienta <strong>{toolToDelete?.display_name}</strong> y no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
